import {
  convertMemberAliasToResponse,
  Id,
  Member,
  MemberAlias,
  MembersData,
} from '@nextforce/api'
import { t } from 'app/i18l'
import { useCreateDirectConversation } from 'chat/conversations/queries/create_direct'
import { useConversation } from 'chat/conversations/queries/detail'
import { ChatGroupMembersScreenProps } from 'home/stack/types'
import { useCallback, useMemo } from 'react'
import { confirmationAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { useUser } from 'users/queries/detail'
import { useMembersWithAlias } from './queries/list'
import { useUpdateMemberAdminStatus, useUpdateMembers } from './queries/update'
import { ChatGroupMembersView } from './views'
import { getAliasDisplayName } from './views/member_alias'

export function ChatGroupMembersScreen({
  navigation,
  route,
}: ChatGroupMembersScreenProps) {
  const { conversationId, search } = route.params
  const membersQuery = useMembersWithAlias(conversationId)
  const { data: user } = useUser()

  const conversationQuery = useConversation(conversationId)

  const createDirectConversationMutation = useCreateDirectConversation()
  const updateMembersMutation = useUpdateMembers()
  const updateMemberAdminStatusMutation = useUpdateMemberAdminStatus()

  const conversation = conversationQuery.data

  const members = useMemo(() => {
    let conversationMembers: MembersData = []

    if (membersQuery.data) {
      conversationMembers = membersQuery.data
      const userIndex = conversationMembers?.findIndex(
        member => member.id === user?.id
      )
      conversationMembers[userIndex].isAdmin = conversation?.allowEdit
    }

    return conversationMembers
  }, [conversation?.allowEdit, membersQuery.data, user?.id])

  const nonAliasMembers = useMemo(() => {
    return members?.filter(m => !m.isAlias)
  }, [members])

  const memberAliases: MemberAlias[] | undefined = useMemo(() => {
    return members?.filter(m => m.isAlias).map(m => m.firstName as MemberAlias)
  }, [members])

  const onAliasRemoved = useCallback(
    async (alias: MemberAlias) => {
      if (!convertMemberAliasToResponse) {
        return
      }
      const confirmation = await confirmationAlert(
        t('removeAliasConfirmationTitle', {
          alias: getAliasDisplayName(alias),
        }),
        t('removeAliasConfirmationDescription', {
          alias: getAliasDisplayName(alias),
        })
      )
      if (confirmation) {
        const updatedMemberAliases = memberAliases.filter(m => m !== alias)
        await updateMembersMutation.mutateAsync({
          conversationId,
          members: [...nonAliasMembers],
          memberAliases: updatedMemberAliases,
        })
      }
    },
    [conversationId, memberAliases, nonAliasMembers, updateMembersMutation]
  )

  const onRemoveMember = useCallback(
    async (member: Member) => {
      if (!nonAliasMembers) {
        return
      }

      const updatedMembers = nonAliasMembers.filter(
        m => m.id !== member.id && m.id !== user?.id
      )
      await updateMembersMutation.mutateAsync({
        conversationId,
        members: updatedMembers,
        memberAliases: memberAliases,
      })
    },
    [
      conversationId,
      memberAliases,
      nonAliasMembers,
      updateMembersMutation,
      user?.id,
    ]
  )

  const onSendDirectMessage = useCallback(
    async (recipientId: Id) => {
      try {
        const directConversation =
          await createDirectConversationMutation.mutateAsync(recipientId)
        navigation.push('messages', {
          id: directConversation.id,
        })
      } catch (err: any) {
        // FIXME: better send direct error message
        alert(err.message)
      }
    },
    [createDirectConversationMutation, navigation]
  )

  const onToggleAdminStatus = useCallback(
    async (member: Member) => {
      if (!members) {
        return
      }

      const memberIndex = members.findIndex(
        m => m.id === member.id && m.id !== user?.id
      )

      if (memberIndex < 0) {
        return
      }

      await updateMemberAdminStatusMutation.mutateAsync({
        conversationId,
        memberId: member.id,
        adminFlag: !member.isAdmin,
      })
    },
    [conversationId, members, updateMemberAdminStatusMutation, user?.id]
  )

  return (
    <StatusBoundary
      error={membersQuery.error}
      isLoading={membersQuery.isLoading}
    >
      <ChatGroupMembersView
        nonAliasMembers={nonAliasMembers}
        aliasMembers={memberAliases}
        conversation={conversation}
        user={user}
        search={search}
        onRemoveMember={onRemoveMember}
        onSendDirectMessage={onSendDirectMessage}
        onToggleAdminStatus={onToggleAdminStatus}
        onRemoveMemberAlias={onAliasRemoved}
      />
    </StatusBoundary>
  )
}
